import { useId } from "react";

import { cn } from "@/lib/cn";
import { tone, type Tone } from "@/components/ui/tone";

export type ToggleProps = {
  label: string;
  description?: string | undefined;
  checked: boolean;
  disabled?: boolean | undefined;
  tone?: Tone | undefined;
  onChange: (checked: boolean) => void;
};

export function Toggle({
  label,
  description,
  checked,
  disabled = false,
  tone: which,
  onChange,
}: ToggleProps) {
  const t = tone(which);
  const id = useId();
  const describedBy = description === undefined ? undefined : `${id}-description`;

  return (
    <button
      aria-checked={checked}
      aria-describedby={describedBy}
      className={cn(
        "flex w-full items-start justify-between gap-4 rounded-xl border px-3.5 py-3 text-start transition-colors",
        t.line,
        disabled ? "opacity-60" : t.hover,

        checked && "border-brand/60",
      )}
      disabled={disabled}
      onClick={() => {
        onChange(!checked);
      }}
      role="switch"
      type="button"
    >
      <span className="min-w-0">
        <span className={cn("block text-sm font-medium", t.text)}>{label}</span>
        {description !== undefined ? (
          <span className={cn("mt-0.5 block text-xs", t.muted)} id={describedBy}>
            {description}
          </span>
        ) : null}
      </span>

      <span
        aria-hidden
        className={cn(
          "mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full p-0.5 transition-colors",
          checked ? "bg-brand" : t.switchOff,
        )}
      >
        {}
        <span
          className={cn(
            "h-5 w-5 rounded-full bg-white shadow-sm transition-transform",
            checked ? "translate-x-5 rtl:-translate-x-5" : "translate-x-0",
          )}
        />
      </span>
    </button>
  );
}
